
import React from "react";
import { Card, CardContent } from "../../components/ui/card";
import { formatValue, displayLineItemName } from "../../lib/utils";

interface CashFlowTabProps {
  financialData: any;
}

const CashFlowTab: React.FC<CashFlowTabProps> = ({ financialData }) => {
  const financials = financialData || [];
  const years = financials.map((f: any) => {
    const year = f.year.split('-')[0];
    return `FY ${(parseInt(year) - 1).toString()} - ${year.toString().slice(-2)}`;
  });

  const nature = financials.find((f: any) => f.nature)?.nature || null;
  
  // Detect if this is LLPIN data
  const isLLPIN = financials.some((f: any) => 
    (f as any).isLLPIN || 
    (f as any).statement_of_assets_and_liabilities || 
    (f as any).statement_of_income_and_expenditure
  );

  // Group line items by activity type
  const operatingActivities = [
    'profit_before_tax',
    'adjustment_for_finance_cost_and_depreciation',
    'adjustment_for_current_and_non_current_assets',
    'adjustment_for_current_and_non_current_liabilities',
    'other_adjustments_in_operating_activities',
    'cash_flows_from_used_in_operating_activities'
  ];

  const investingActivities = [
    'cash_outflow_from_purchase_of_assets',
    'cash_inflow_from_sale_of_assets',
    'income_from_assets',
    'other_adjustments_in_investing_activities',
    'cash_flows_from_used_in_investing_activities'
  ];

  const financingActivities = [
    'cash_outflow_from_repayment_of_capital_and_borrowings',
    'cash_inflow_from_raisng_capital_and_borrowings',
    'interest_and_dividends_paid',
    'other_adjustments_in_financing_activities',
    'cash_flows_from_used_in_financing_activities'
  ];

  const netCashFlow = [
    'incr_decr_in_cash_cash_equv_before_effect_of_excg_rate_changes',
    'adjustments_to_cash_and_cash_equivalents',
    'incr_decr_in_cash_cash_equv',
    'cash_flow_statement_at_end_of_period'
  ];

  const renderCashFlowSection = (lineItems: string[], title: string, colorClass: string) => (
    <div className="mb-8">
      <h4 className={`text-lg font-semibold mb-4 text-gray-800 flex items-center`}>
        <span className={`inline-block w-4 h-4 ${colorClass} mr-3 rounded-sm`}></span>
        {title}
      </h4>
      <div className="border rounded-lg overflow-hidden bg-white shadow-sm">
      <div className="w-full overflow-x-auto">
  <div
    className="grid"
    style={{
      minWidth: `${240 + years.length * 160}px`, // dynamically adjust min-width
      gridTemplateColumns: `minmax(240px,1fr) repeat(${years.length}, minmax(160px,1fr))`,
    }}
  >


            {/* Header Row */}
            <div className="bg-gray-50 p-4 font-semibold border-b border-r sticky left-0 z-20">
              Line Item
            </div>
            {years.map((year, index) => (
              <div key={index} className="bg-gray-50 p-4 font-semibold text-center border-b border-r">
                {year}
              </div>
            ))}

            {/* Data Rows */}
            {lineItems.map((lineItem) => (
              <React.Fragment key={lineItem}>
                {/* Line Item Name (Frozen Column) */}
                <div
                  className={`p-4 border-b border-r sticky left-0 z-10 bg-white ${lineItem.includes('cash_flows_from_used') || lineItem.includes('cash_flow_statement_at_end') || lineItem.includes('incr_decr_in_cash_cash_equv')
                      ? 'font-semibold bg-blue-50'
                      : ''
                    }`}
                >
                  {displayLineItemName(lineItem, isLLPIN)}
                </div>

                {/* Values for each year */}
                {financials.map((financial: any, colIndex: number) => {
                  const value = financial.cash_flow?.[lineItem];

                  return (
                    <div
                      key={`${lineItem}-${colIndex}`}
                      className={`p-4 text-right border-b border-r ${lineItem.includes('cash_flows_from_used') || lineItem.includes('cash_flow_statement_at_end') || lineItem.includes('incr_decr_in_cash_cash_equv')
                          ? 'font-semibold bg-blue-50'
                          : ''
                        }`}
                    >
                      {formatValue(value)}
                    </div>
                  );
                })}
              </React.Fragment>
            ))}
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <Card>
      <CardContent className="pt-6">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-2xl font-bold text-gray-900">Cash Flow Statement</h3>
          {/* <p className="truncate break-words max-w-xs hover:overflow-visible hover:whitespace-normal hover:break-words">
            Financial Years: {years.join(', ')}
          </p> */}
          <h3>{nature}</h3>
        </div>

        {/* Operating Activities */}
        {renderCashFlowSection(operatingActivities, '💼 Operating Activities', 'bg-green-500')}

        {/* Investing Activities */}
        {renderCashFlowSection(investingActivities, '🏭 Investing Activities', 'bg-yellow-500')}

        {/* Financing Activities */}
        {renderCashFlowSection(financingActivities, '💰 Financing Activities', 'bg-red-500')}

        {/* Net Cash Flow */}
        {renderCashFlowSection(netCashFlow, '📊 Net Cash Flow', 'bg-blue-500')}

        {/* Summary Cards */}
        {/* <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <h5 className="font-semibold text-green-800 mb-2">Operating Cash Flow</h5>
            <p className="text-2xl font-bold text-green-700">
              {formatValue(financials[0]?.cash_flow?.cash_flows_from_used_in_operating_activities)}
            </p>
          </div>
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <h5 className="font-semibold text-yellow-800 mb-2">Investing Cash Flow</h5>
            <p className="text-2xl font-bold text-yellow-700">
              {formatValue(financials[0]?.cash_flow?.cash_flows_from_used_in_investing_activities)}
            </p>
          </div>
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h5 className="font-semibold text-blue-800 mb-2">Cash at End of Period</h5>
            <p className="text-2xl font-bold text-blue-700">
              {formatValue(financials[0]?.cash_flow?.cash_flow_statement_at_end_of_period)}
            </p>
          </div>
        </div> */}
      </CardContent>
    </Card>
  );
};

export default CashFlowTab;
